/**
 * AdGraphicEditor - Canvas-based ad graphic generator
 * Overlays text on campaign images with 3 preset styles
 * Supports position control and JPG export
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  X, Download, Type, AlignCenter, AlignStartVertical,
  AlignEndVertical, Loader2, Paintbrush, Check
} from 'lucide-react';

const STYLES = [
  {
    id: 'classic',
    label: 'Klasyczny',
    desc: 'Biały tekst, ciemny pasek',
    textColor: '#FFFFFF',
    bgColor: 'rgba(0,0,0,0.65)',
    fontSize: 36,
    fontWeight: '700',
    fontFamily: 'Arial, Helvetica, sans-serif',
    padding: 24,
    bgHeight: 0.25,
  },
  {
    id: 'bold',
    label: 'Bold',
    desc: 'Duży tekst na środku',
    textColor: '#FFFFFF',
    bgColor: 'rgba(0,0,0,0.5)',
    fontSize: 48,
    fontWeight: '900',
    fontFamily: 'Arial Black, Arial, sans-serif',
    padding: 32,
    bgHeight: 1,
  },
  {
    id: 'minimal',
    label: 'Minimalistyczny',
    desc: 'Subtelny tekst w rogu',
    textColor: '#FFFFFF',
    bgColor: 'rgba(0,0,0,0.35)',
    fontSize: 24,
    fontWeight: '600',
    fontFamily: 'Arial, Helvetica, sans-serif',
    padding: 16,
    bgHeight: 0.15,
  },
];

const POSITIONS = [
  { id: 'top', label: 'Góra', icon: AlignStartVertical },
  { id: 'center', label: 'Środek', icon: AlignCenter },
  { id: 'bottom', label: 'Dół', icon: AlignEndVertical },
];

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function wrapText(ctx, text, maxWidth, fontSize) {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? currentLine + ' ' + word : word;
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

export default function AdGraphicEditor({ imageUrl, onClose, cardType }) {
  const canvasRef = useRef(null);
  const [text, setText] = useState('');
  const [activeStyle, setActiveStyle] = useState('classic');
  const [position, setPosition] = useState('bottom');
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Load image
  useEffect(() => {
    if (!imageUrl) return;
    setLoading(true);
    loadImage(imageUrl)
      .then(img => { setImage(img); setLoading(false); })
      .catch(() => { toast.error('Nie udało się załadować zdjęcia'); setLoading(false); });
  }, [imageUrl]);

  // Render canvas
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    const w = 1200;
    const h = Math.round((image.height / image.width) * w);
    canvas.width = w;
    canvas.height = h;

    // Draw image
    ctx.drawImage(image, 0, 0, w, h);

    if (!text.trim()) return;

    const style = STYLES.find(s => s.id === activeStyle) || STYLES[0];
    const maxTextWidth = w - style.padding * 2;

    // Set font
    ctx.font = `${style.fontWeight} ${style.fontSize}px ${style.fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const lines = wrapText(ctx, text.trim(), maxTextWidth, style.fontSize);
    const lineHeight = style.fontSize * 1.3;
    const totalTextHeight = lines.length * lineHeight;

    // Calculate Y position
    let bgY, textStartY;
    const bgPadding = style.padding;

    if (position === 'top') {
      bgY = 0;
      const bgH = style.bgHeight === 1 ? h : Math.max(totalTextHeight + bgPadding * 2, h * style.bgHeight);
      textStartY = bgPadding + lineHeight / 2;
      // Draw background
      ctx.fillStyle = style.bgColor;
      ctx.fillRect(0, bgY, w, bgH);
    } else if (position === 'center') {
      const bgH = style.bgHeight === 1 ? h : Math.max(totalTextHeight + bgPadding * 2, h * 0.3);
      bgY = (h - bgH) / 2;
      textStartY = bgY + (bgH - totalTextHeight) / 2 + lineHeight / 2;
      ctx.fillStyle = style.bgColor;
      ctx.fillRect(0, bgY, w, bgH);
    } else {
      // bottom
      const bgH = style.bgHeight === 1 ? h : Math.max(totalTextHeight + bgPadding * 2, h * style.bgHeight);
      bgY = h - bgH;
      textStartY = bgY + bgPadding + lineHeight / 2;
      ctx.fillStyle = style.bgColor;
      ctx.fillRect(0, bgY, w, bgH);
    }

    // Draw text with shadow
    ctx.fillStyle = style.textColor;
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;

    lines.forEach((line, i) => {
      ctx.fillText(line, w / 2, textStartY + i * lineHeight);
    });

    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
  }, [image, text, activeStyle, position]);

  useEffect(() => { renderCanvas(); }, [renderCanvas]);

  const handleExport = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setExporting(true);
    try {
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `apisai-grafika-${cardType || 'ad'}.jpg`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Grafika pobrana!');
    } catch {
      toast.error('Nie udało się pobrać');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="ad-graphic-editor">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden flex flex-col"
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b flex-shrink-0" style={{ borderColor: 'var(--apisai-border)' }}>
          <div className="flex items-center gap-2">
            <Paintbrush className="w-4 h-4" style={{ color: 'var(--apisai-primary)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--apisai-text)' }}>Generator grafiki reklamowej</span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-lg">
            <X className="w-4 h-4 text-zinc-500" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Canvas Preview */}
          <div className="flex-1 bg-gray-900 flex items-center justify-center p-4 overflow-auto">
            {loading ? (
              <Loader2 className="w-8 h-8 animate-spin text-white" />
            ) : (
              <canvas
                ref={canvasRef}
                className="max-w-full max-h-full rounded-lg shadow-lg"
                style={{ width: '100%', height: 'auto', maxHeight: '60vh', objectFit: 'contain' }}
                data-testid="ad-canvas"
              />
            )}
          </div>

          {/* Controls */}
          <div className="w-72 border-l flex-shrink-0 overflow-y-auto p-4 space-y-5" style={{ borderColor: 'var(--apisai-border)' }}>
            {/* Text input */}
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium mb-2" style={{ color: 'var(--apisai-text-secondary)' }}>
                <Type className="w-3.5 h-3.5" /> Tekst na grafice
              </label>
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="np. Dostępne szczenięta"
                rows={3}
                className="w-full px-3 py-2 rounded-xl border text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-200"
                style={{ borderColor: 'var(--apisai-border)' }}
                data-testid="ad-text-input"
              />
            </div>

            {/* Style selection */}
            <div>
              <p className="text-xs font-medium mb-2" style={{ color: 'var(--apisai-text-secondary)' }}>Styl napisu</p>
              <div className="space-y-1.5">
                {STYLES.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setActiveStyle(s.id)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border text-left transition-all"
                    style={{
                      borderColor: activeStyle === s.id ? 'var(--apisai-primary)' : 'var(--apisai-border)',
                      background: activeStyle === s.id ? '#FFF7ED' : 'white',
                    }}
                    data-testid={`style-${s.id}`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: s.id === 'classic' ? '#1a1a1a' : s.id === 'bold' ? '#333' : '#666' }}>
                      <span className="text-white text-[10px] font-bold">Aa</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium" style={{ color: activeStyle === s.id ? 'var(--apisai-primary)' : 'var(--apisai-text)' }}>{s.label}</p>
                      <p className="text-[10px]" style={{ color: 'var(--apisai-text-muted)' }}>{s.desc}</p>
                    </div>
                    {activeStyle === s.id && <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--apisai-primary)' }} />}
                  </button>
                ))}
              </div>
            </div>

            {/* Position */}
            <div>
              <p className="text-xs font-medium mb-2" style={{ color: 'var(--apisai-text-secondary)' }}>Pozycja tekstu</p>
              <div className="flex gap-1.5">
                {POSITIONS.map(p => {
                  const Icon = p.icon;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setPosition(p.id)}
                      className="flex-1 flex flex-col items-center gap-1 py-2 rounded-xl border transition-all"
                      style={{
                        borderColor: position === p.id ? 'var(--apisai-primary)' : 'var(--apisai-border)',
                        background: position === p.id ? '#FFF7ED' : 'white',
                        color: position === p.id ? 'var(--apisai-primary)' : 'var(--apisai-text-muted)',
                      }}
                      data-testid={`position-${p.id}`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span className="text-[10px] font-medium">{p.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quick text suggestions */}
            <div>
              <p className="text-xs font-medium mb-2" style={{ color: 'var(--apisai-text-secondary)' }}>Szybkie napisy</p>
              <div className="flex flex-wrap gap-1.5">
                {['Dostępne szczenięta', 'Rezerwacja otwarta', 'Umów wizytę', 'Promocja -20%', 'Sprawdź ofertę', 'Nowa kolekcja'].map(suggestion => (
                  <button
                    key={suggestion}
                    onClick={() => setText(suggestion)}
                    className="px-2.5 py-1 rounded-lg text-[10px] font-medium border hover:bg-zinc-50 transition-colors"
                    style={{ borderColor: 'var(--apisai-border)', color: 'var(--apisai-text-secondary)' }}
                    data-testid={`suggestion-${suggestion.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>

            {/* Export */}
            <Button
              onClick={handleExport}
              disabled={exporting || !text.trim()}
              className="w-full rounded-xl h-10 text-xs font-medium text-white"
              style={{ background: 'var(--apisai-primary)' }}
              data-testid="export-graphic"
            >
              {exporting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <><Download className="w-3.5 h-3.5 mr-1.5" /> Pobierz grafikę</>
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
